import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useActividades, useActualizarActividad, useCrearActividad } from '@/hooks/useLaboratorio';
import { cn } from '@/lib/utils';
import type { Actividad } from '@adelante/shared';
import { Check, Pencil, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';

/**
 * `/admin/actividades` — gestión del catálogo de actividades del laboratorio
 * (AZOTEA, CALLES, COLUMNAS, MUROS, …). Solo Admin.
 *
 * Las actividades clasifican cada muestra (campo obligatorio al crearla).
 * Desactivar una actividad la saca del dropdown de "Nueva muestra" pero NO
 * borra las muestras que ya la usan (es un soft-delete vía flag `activo`).
 *
 * El check de rol Admin es del backend (POST/PATCH exigen ROLES_ADMIN). Acá
 * no protegemos la ruta: si un no-admin llega, las requests fallan con 403.
 */

export function AdminActividadesPantalla() {
  const [incluirInactivas, setIncluirInactivas] = useState(false);
  const consulta = useActividades(incluirInactivas);
  const crear = useCrearActividad();
  const actualizar = useActualizarActividad();

  const actividades = consulta.data?.data?.actividades ?? [];
  const errorBackend = consulta.data?.error?.mensaje ?? null;

  // Form "nueva actividad".
  const [nombre, setNombre] = useState('');
  const [orden, setOrden] = useState('');

  // Sugerencia de orden: el mayor existente + 10 (para que quede al final).
  const ordenSugerido = useMemo(() => {
    const max = actividades.reduce((m, a) => Math.max(m, a.orden), 0);
    return max + 10;
  }, [actividades]);

  // Edición inline.
  const [editId, setEditId] = useState<number | null>(null);
  const [editNombre, setEditNombre] = useState('');
  const [editOrden, setEditOrden] = useState('');

  const handleCrear = () => {
    const nombreTrim = nombre.trim();
    if (!nombreTrim) return;
    crear.mutate(
      { nombre: nombreTrim, orden: orden.trim() === '' ? ordenSugerido : Number(orden) },
      {
        onSuccess: (r) => {
          if (r.data) {
            setNombre('');
            setOrden('');
          }
        },
      },
    );
  };

  const iniciarEdicion = (a: Actividad) => {
    setEditId(a.id);
    setEditNombre(a.nombre);
    setEditOrden(String(a.orden));
  };

  const cancelarEdicion = () => setEditId(null);

  const guardarEdicion = () => {
    if (editId === null) return;
    const nombreTrim = editNombre.trim();
    if (!nombreTrim) return;
    actualizar.mutate(
      { id: editId, nombre: nombreTrim, orden: editOrden.trim() === '' ? 0 : Number(editOrden) },
      {
        onSuccess: (r) => {
          if (r.data) setEditId(null);
        },
      },
    );
  };

  const toggleActivo = (a: Actividad) => {
    if (
      a.activo &&
      !confirm(
        `¿Desactivar "${a.nombre}"?\n\nDejará de aparecer al crear muestras nuevas. Las muestras existentes no se ven afectadas.`,
      )
    ) {
      return;
    }
    actualizar.mutate({ id: a.id, activo: !a.activo });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Actividades de laboratorio</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Catálogo de actividades (AZOTEA, CALLES, COLUMNAS, MUROS…) que clasifican cada muestra. El
          orden controla cómo aparecen en el listado al crear una muestra.
        </p>
      </div>

      {/* Nueva actividad */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Nueva actividad</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="grid flex-1 min-w-[220px] gap-1.5">
            <Label htmlFor="act-nombre">Nombre</Label>
            <Input
              id="act-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCrear();
              }}
              placeholder="Ej: LOSA FUNDACIÓN"
              maxLength={100}
            />
          </div>
          <div className="grid w-[110px] gap-1.5">
            <Label htmlFor="act-orden">Orden</Label>
            <Input
              id="act-orden"
              type="number"
              value={orden}
              onChange={(e) => setOrden(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCrear();
              }}
              placeholder={String(ordenSugerido)}
            />
          </div>
          <Button
            onClick={handleCrear}
            disabled={!nombre.trim() || crear.isPending}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {crear.isPending ? 'Creando…' : 'Crear'}
          </Button>
        </CardContent>
      </Card>

      {errorBackend && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm text-destructive">{errorBackend}</CardContent>
        </Card>
      )}

      {/* Listado */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
          <CardTitle className="text-sm font-medium">
            Catálogo{' '}
            <span className="font-normal text-muted-foreground">({actividades.length})</span>
          </CardTitle>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={incluirInactivas}
              onChange={(e) => setIncluirInactivas(e.target.checked)}
              className="h-4 w-4"
            />
            Mostrar inactivas
          </label>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[90px]">Orden</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead className="w-[110px]">Estado</TableHead>
                <TableHead className="w-[200px] text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {consulta.isLoading &&
                ['s-a', 's-b', 's-c'].map((id) => (
                  <TableRow key={id}>
                    {['c1', 'c2', 'c3', 'c4'].map((c) => (
                      <TableCell key={`${id}-${c}`}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!consulta.isLoading && actividades.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    No hay actividades. Creá la primera con el formulario de arriba.
                  </TableCell>
                </TableRow>
              )}

              {!consulta.isLoading &&
                actividades.map((a) => {
                  const enEdicion = editId === a.id;
                  return (
                    <TableRow key={a.id} className={cn(!a.activo && 'opacity-60')}>
                      <TableCell className="tabular-nums">
                        {enEdicion ? (
                          <Input
                            type="number"
                            value={editOrden}
                            onChange={(e) => setEditOrden(e.target.value)}
                            className="h-8 w-[80px]"
                          />
                        ) : (
                          a.orden
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {enEdicion ? (
                          <Input
                            value={editNombre}
                            onChange={(e) => setEditNombre(e.target.value.toUpperCase())}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') guardarEdicion();
                              if (e.key === 'Escape') cancelarEdicion();
                            }}
                            className="h-8"
                            maxLength={100}
                          />
                        ) : (
                          a.nombre
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'inline-flex rounded-full border px-2 py-0.5 text-xs',
                            a.activo
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                              : 'border-zinc-300 bg-zinc-100 text-zinc-500',
                          )}
                        >
                          {a.activo ? 'Activa' : 'Inactiva'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {enEdicion ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8"
                              disabled={!editNombre.trim() || actualizar.isPending}
                              onClick={guardarEdicion}
                              title="Guardar"
                              aria-label="Guardar"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={cancelarEdicion}
                              title="Cancelar"
                              aria-label="Cancelar"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 gap-1.5"
                              onClick={() => iniciarEdicion(a)}
                              disabled={actualizar.isPending}
                            >
                              <Pencil className="h-3.5 w-3.5" /> Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              onClick={() => toggleActivo(a)}
                              disabled={actualizar.isPending}
                            >
                              {a.activo ? 'Desactivar' : 'Activar'}
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
